import { Md5 } from 'ts-md5';
import { TypeCampaignEnum } from '@/types';
import type { FlowOperatorMetadata } from '@/types/flow-types';

export interface CustomJSData {
  token_script?: string;
}

export function createDefaultData(data: Partial<CustomJSData> = {}): CustomJSData {
  return {
    token_script: data.token_script ?? '',
  };
}

const BARE_PUSH_TOKEN_SCRIPT = /^spm_wtk\.push\("([a-zA-Z0-9]+)"\);$/;

// Goes through `window` so the token reaches the tag's global array even when
// the snippet is pasted inside a function or loaded before the tag.
function buildTokenScript(token: string): string {
  return `window.spm_wtk=window.spm_wtk||[];window.spm_wtk.push("${token}");`;
}

function generateTokenScript(): string {
  const getRandom = () => Math.random() * Number.MAX_SAFE_INTEGER;
  return buildTokenScript(Md5.hashStr(`${Date.now().toString()}-${getRandom()}-${getRandom()}`));
}

/** Rebuilds bare `spm_wtk.push(...)` scripts, which throw before the tag, keeping their token. */
export function normalizeTokenScript(script: string): string {
  const match = script.match(BARE_PUSH_TOKEN_SCRIPT);
  return match ? buildTokenScript(match[1]) : script;
}

export function ensureTokenScript(script = ''): string {
  return script ? normalizeTokenScript(script) : generateTokenScript();
}

/**
 * Rules-only hidden operator for the `custom_js` internal event. Like the other
 * former trigger boxes (boxvisitpage, boxsitepasstime…), it carries the
 * chaining incompatibilities of its event so the compatibility engine can
 * resolve event `custom_js` → `boxcustomjs` and apply the V4 rules. The config
 * UI is handled by TriggerInternalEvent, so this operator needs no Form.
 */
const CustomJS: FlowOperatorMetadata<CustomJSData> = {
  id: 'boxcustomjs',
  kind: 'trigger',
  label: 'automatedScenarios.triggers.customJS.label',
  icon: 'fa-flask',
  hasConfiguration: true,
  availableIn: [],
  hidden: true,

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxabandonpanier', 'boxperiodiclaunching', 'boxstatuscommand',
        'boxcustomjs', 'boxsendemail', 'boxsendsms', 'boxsendfbmessenger',
        'boxsendpushnotification', 'openemail', 'clicklink',
        'boxtriggerinternalevent', 'boxsendrcs', 'boxsendwhatsapp',
      ],
    },
  },

  defaultData: createDefaultData,

  outputs: () => 1,
};

export default CustomJS;
